import { UserTierType } from "@/constants/enums/user-tier-type.enum";
import { FormikErrors, FormikProps } from "formik";
import { CommodityType, CompanyDetails, UpdateValues } from "./index";
import { FormControl, MenuItem, Select, TextField } from "@mui/material";
import { memo, useEffect, useState } from "react";
import DynamicAutocomplete from "@/components/common/inputs/select/DynamicAutocomplete";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import AmountInput from "@/components/common/inputs/NumberInput/AmountInput";

interface RenderOtherDetailsProps {
  userType: UserTierType;
  formik: FormikProps<UpdateValues>;
  otherDetails: any;
  onOtherDetailsChange: (details: any) => void;
}

const RenderOtherDetails: React.FC<RenderOtherDetailsProps> = memo(
  ({ userType, formik, otherDetails, onOtherDetailsChange }) => {
    const dispatch = useDispatch<AppDispatch>();
    const [debtorTypes, setDebtorTypes] = useState<any[]>([]);
    const [paymentTerms, setPaymentTerms] = useState<any[]>([]);
    const [clientTypes, setClientTypes] = useState<any[]>([]);
    const [professions, setProfessions] = useState<any[]>([]);
    const [commodityTypes, setCommodityTypes] = useState<any[]>([]);

    useEffect(() => {
      const fetchData = async () => {
        try {
          // Common API calls for all tiers
          const { authenticatedCall: fetchCommodities } =
            actionsHandler("commodityList");
          const commodityResponse = await dispatch(
            fetchCommodities({ method: "get" }),
          );
          if (commodityResponse.ok) {
            setCommodityTypes(commodityResponse.data);
          }

          // Tier-specific API calls
          switch (userType) {
            case UserTierType.Tier5:
              const { authenticatedCall: fetchDebtorTypes } =
                actionsHandler("debtorTypes");
              const { authenticatedCall: fetchPaymentTerms } =
                actionsHandler("paymentTermsList");
              const { authenticatedCall: fetchClientTypes } =
                actionsHandler("clientTypes");

              const [debtorResponse, paymentResponse, clientResponse] =
                await Promise.all([
                  dispatch(fetchDebtorTypes({ method: "get" })),
                  dispatch(fetchPaymentTerms({ method: "get" })),
                  dispatch(fetchClientTypes({ method: "get" })),
                ]);

              if (debtorResponse.ok) setDebtorTypes(debtorResponse.data);
              if (paymentResponse.ok) setPaymentTerms(paymentResponse.data);
              if (clientResponse.ok) setClientTypes(clientResponse.data);
              break;

            case UserTierType.Tier6:
              const { authenticatedCall: fetchProfessions } =
                actionsHandler("professions");
              const professionResponse = await dispatch(
                fetchProfessions({ method: "get" }),
              );
              if (professionResponse.ok) {
                setProfessions(professionResponse.data);
              }
              break;

            // Add other tier-specific API calls as needed
          }
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      };

      fetchData();
    }, [userType, dispatch]);

    const handleFieldChange = (field: string, value: any) => {
      console.log('field: ', field);
      console.log('value: ', value);
      const updatedValue = {
        ...otherDetails,
        [field]: value,
      };
      onOtherDetailsChange(updatedValue);
    };

    switch (userType) {
      case UserTierType.Tier4:
        return (
          <div className="">
            <div className="tw-grid tw-grid-cols-3 tw-gap-6">
              <div>
                <label className="tw-block tw-text-sm font-gray-color-dark tw-font-medium tw-mb-2">
                  Facility Required?
                </label>
                <div className="tw-mt-2">
                  <label className="tw-mr-4">
                    <input
                      type="radio"
                      name="companies.0.facility_required"
                      value="true"
                      checked={otherDetails?.facility_required === true}
                      onChange={(e) =>
                        handleFieldChange(
                          "facility_required",
                          e.target.value === "true",
                        )
                      }
                      className="tw-mr-2"
                    />
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="companies.0.facility_required"
                      value="false"
                      checked={otherDetails?.facility_required === false}
                      onChange={(e) =>
                        handleFieldChange(
                          "facility_required",
                          e.target.value === "true",
                        )
                      }
                      className="tw-mr-2"
                    />
                    No
                  </label>
                </div>
              </div>
              {otherDetails?.facility_required && (
                <div className="form-group">
                  <label className="tw-block tw-text-sm font-gray-color-dark tw-font-medium tw-mb-2">
                    Facility Amount
                  </label>
                  {/* <TextField
                    fullWidth
                    type="number"
                    name="companies.0.facility_amount"
                    variant="outlined"
                    placeholder="Enter Amount"
                    className="tw-w-full tw-h-11 tw-px-3 tw-border tw-border-[#D5D7DA] tw-rounded-md tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-blue-500"
                    value={otherDetails?.facility_amount}
                    onChange={(e) =>
                      handleFieldChange("facility_amount", e.target.value)
                    }
                    error={Boolean(
                      (
                        formik.errors
                          .companies?.[0] as FormikErrors<CompanyDetails>
                      )?.facility_amount,
                    )}
                    helperText={
                      (
                        formik.errors
                          .companies?.[0] as FormikErrors<CompanyDetails>
                      )?.facility_amount
                    }
                  /> */}
                  <AmountInput
                    value={otherDetails?.facility_amount}
                    onChange={(value) => {
                      handleFieldChange("facility_amount", value)
                    }}
                    name="companies.0.facility_amount"
                    error={Boolean(
                      (
                        formik.errors
                          .companies?.[0] as FormikErrors<CompanyDetails>
                      )?.facility_amount,
                    )}
                    helperText={
                      (
                        formik.errors
                          .companies?.[0] as FormikErrors<CompanyDetails>
                      )?.facility_amount
                    }
                  />
                </div>
              )}
            </div>
          </div>
        );

      case UserTierType.Tier5:
        return (
          <div className="">
            <div className="tw-grid lg:tw-grid-cols-3 md:tw-grid-cols-2 lg:tw-gap-6 md:tw-gap-4 tw-gap-2">
              <div className="form-group">
                <DynamicAutocomplete
                  label="Debtor Type"
                  name="companies.0.debtor_type"
                  options={debtorTypes}
                  selectedValue={otherDetails?.debtor_type?.id || null}
                  labelKey="debtor_type"
                  valueKey="id"
                  onChange={(value: any) => {
                    handleFieldChange(
                      "debtor_type",
                      debtorTypes?.find((debtor) => debtor?.id === value) ||
                      null,
                    );
                  }}
                  placeholder="Select Debtor Type"
                  error={
                    Boolean(
                      (
                        formik.errors
                          .companies?.[0] as FormikErrors<CompanyDetails>
                      )?.debtor_type,
                    )
                      ? String(
                        (
                          formik.errors
                            .companies?.[0] as FormikErrors<CompanyDetails>
                        )?.debtor_type,
                      )
                      : ""
                  }
                />
              </div>
              <div className="form-group">
                <DynamicAutocomplete
                  label="Payment Terms"
                  name="companies.0.payment_terms"
                  options={paymentTerms}
                  selectedValue={otherDetails?.payment_terms?.id || null}
                  filterOptions={(options, { inputValue }) =>
                    options.filter((option) =>
                      option.label
                        ?.toLowerCase()
                        .includes(inputValue?.toLowerCase() || ""),
                    )
                  }
                  labelKey="payment_term"
                  valueKey="id"
                  onChange={(value: any) => {
                    handleFieldChange(
                      "payment_terms",
                      paymentTerms?.find((term) => term?.id === value) || null,
                    );
                  }}
                  touched={Boolean(
                    formik.touched.companies?.[0]?.payment_terms,
                  )}
                  placeholder="Select Payment Terms"
                  error={
                    Boolean(
                      (
                        formik.errors
                          .companies?.[0] as FormikErrors<CompanyDetails>
                      )?.payment_terms,
                    )
                      ? String(
                        (
                          formik.errors
                            .companies?.[0] as FormikErrors<CompanyDetails>
                        )?.payment_terms,
                      )
                      : ""
                  }
                />
              </div>

              <div className="form-group">
                <DynamicAutocomplete
                  label="Client Type"
                  name="companies.0.client_type"
                  options={clientTypes}
                  filterOptions={(options, { inputValue }) =>
                    options.filter((option) =>
                      option.label
                        ?.toLowerCase()
                        .includes(inputValue?.toLowerCase() || ""),
                    )
                  }
                  selectedValue={otherDetails?.client_type?.id || null}
                  labelKey="client_type"
                  valueKey="id"
                  onChange={(value: any) => {
                    handleFieldChange(
                      "client_type",
                      clientTypes?.find((client) => client?.id === value) ||
                      null,
                    );
                  }}
                  touched={Boolean(formik.touched.companies?.[0]?.client_type)}
                  placeholder="Select Client Type"
                  error={
                    Boolean(
                      (
                        formik.errors
                          .companies?.[0] as FormikErrors<CompanyDetails>
                      )?.client_type,
                    )
                      ? String(
                        (
                          formik.errors
                            .companies?.[0] as FormikErrors<CompanyDetails>
                        )?.client_type,
                      )
                      : ""
                  }
                />
              </div>
            </div>
          </div>
        );

      case UserTierType.Tier6:
        return (
          <div className="">
            <div className="tw-grid lg:tw-grid-cols-3 md:tw-grid-cols-2 lg:tw-gap-6 md:tw-gap-4 tw-gap-2">
              <div className="form-group">
                <DynamicAutocomplete
                  label="Profession"
                  name="companies.0.profession"
                  options={professions}
                  filterOptions={(options, { inputValue }) =>
                    options.filter((option) =>
                      option.label
                        ?.toLowerCase()
                        .includes(inputValue?.toLowerCase() || ""),
                    )
                  }
                  selectedValue={otherDetails?.profession?.id || null}
                  labelKey="title"
                  valueKey="id"
                  onChange={(value: any) => {
                    handleFieldChange(
                      "profession",
                      professions?.find(
                        (profession) => profession?.id === value,
                      ) || null,
                    );
                  }}
                  touched={Boolean(formik.touched.companies?.[0]?.profession)}
                  placeholder="Select Client Type"
                  error={
                    Boolean(
                      (
                        formik.errors
                          .companies?.[0] as FormikErrors<CompanyDetails>
                      )?.profession,
                    )
                      ? String(
                        (
                          formik.errors
                            .companies?.[0] as FormikErrors<CompanyDetails>
                        )?.profession,
                      )
                      : ""
                  }
                />
              </div>

              <div className="form-group">
                <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                  Why do you want to access Sentinel?
                </p>
                <TextField
                  fullWidth
                  name="companies.0.reason_to_access"
                  placeholder="Enter your reason"
                  value={otherDetails?.reason_to_access}
                  onChange={(e) =>
                    handleFieldChange("reason_to_access", e.target.value)
                  }
                />
              </div>
            </div>
          </div>
        );

      case UserTierType.Tier7:
        return (
          <div className="">
            <div className="tw-grid lg:tw-grid-cols-3 md:tw-grid-cols-2 lg:tw-gap-6 md:tw-gap-4 tw-gap-2">
              <div className="form-group">
                <DynamicAutocomplete
                  label="Profession"
                  name="companies.0.profession"
                  options={professions}
                  filterOptions={(options, { inputValue }) =>
                    options.filter((option) =>
                      option.label
                        ?.toLowerCase()
                        .includes(inputValue?.toLowerCase() || ""),
                    )
                  }
                  selectedValue={otherDetails?.profession?.id || null}
                  labelKey="title"
                  valueKey="id"
                  onChange={(value: any) => {
                    handleFieldChange(
                      "profession",
                      professions?.find(
                        (profession) => profession?.id === value,
                      ) || null,
                    );
                  }}
                  touched={Boolean(formik.touched.companies?.[0]?.profession)}
                  placeholder="Select Client Type"
                  error={
                    Boolean(
                      (
                        formik.errors
                          .companies?.[0] as FormikErrors<CompanyDetails>
                      )?.profession,
                    )
                      ? String(
                        (
                          formik.errors
                            .companies?.[0] as FormikErrors<CompanyDetails>
                        )?.profession,
                      )
                      : ""
                  }
                />
              </div>

              <div className="form-group">
                <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                  Why do you want to access Sentinel?
                </p>
                <TextField
                  fullWidth
                  name="companies.0.reason_to_access"
                  placeholder="Enter your reason"
                  value={otherDetails?.reason_to_access}
                  onChange={(e) =>
                    handleFieldChange("reason_to_access", e.target.value)
                  }
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  },
);

RenderOtherDetails.displayName = "RenderOtherDetails";

export default RenderOtherDetails;
